import type { PovCharacterAnchor } from "./anchor";

export interface PovSmoothState {
  x: number;
  y: number;
  povId: string;
  /** How far it still is from the new POV character. Absent once it has arrived. */
  gap?: { x: number; y: number };
}

/** Per-frame glide when the POV character changes. */
const HANDOFF_EASE_FACTOR = 0.32;

const SNAP_RESIDUAL_PX = 0.45;

/** The frame that factor is tuned for. A slower frame closes more, so the glide keeps time. */
const TUNED_FRAME_MS = 1000 / 60;

/**
 * The gap to the character closes, rather than chasing where the character is.
 * Chasing someone who keeps moving never arrives, and the badge trails behind for good.
 */
export function easedPovAnchor(
  state: PovSmoothState | null,
  target: PovCharacterAnchor,
  povId: string,
  elapsedMs = TUNED_FRAME_MS,
): { anchor: PovCharacterAnchor; state: PovSmoothState } {
  const keep = (1 - HANDOFF_EASE_FACTOR) ** (Math.max(0, elapsedMs) / TUNED_FRAME_MS);
  if (state === null) {
    return settled(target, povId);
  }
  if (state.povId !== povId) {
    return closeGap({ x: state.x - target.anchorX, y: state.y - target.topY }, target, povId, keep);
  }
  return state.gap === undefined
    ? settled(target, povId)
    : closeGap(state.gap, target, povId, keep);
}

function closeGap(
  from: { x: number; y: number },
  target: PovCharacterAnchor,
  povId: string,
  keep: number,
): { anchor: PovCharacterAnchor; state: PovSmoothState } {
  const gap = { x: from.x * keep, y: from.y * keep };
  if (Math.hypot(gap.x, gap.y) <= SNAP_RESIDUAL_PX) {
    return settled(target, povId);
  }
  const anchorX = target.anchorX + gap.x;
  const topY = target.topY + gap.y;
  return {
    anchor: { anchorX, topY },
    state: { x: anchorX, y: topY, povId, gap },
  };
}

function settled(
  target: PovCharacterAnchor,
  povId: string,
): { anchor: PovCharacterAnchor; state: PovSmoothState } {
  return {
    anchor: target,
    state: { x: target.anchorX, y: target.topY, povId },
  };
}
